import { useMemo, useCallback, useState, useEffect } from 'react';
import {
  CombinatorRendererProps,
  createCombinatorRenderInfos,
  createDefaultValue,
  JsonSchema,
  isOneOfControl,
  rankWith,
  ControlElement,
  Resolve,
} from '@jsonforms/core';
import { withJsonFormsOneOfProps } from '@jsonforms/react';
import {
  Box,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { StrategyConfigForm } from '../OneOfDropdownRenderer/OneOfDropdownRenderer';

function stripOptionalProperties(data: unknown, branchSchema: JsonSchema): unknown {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data;
  }
  const obj = data as Record<string, unknown>;
  const props = (branchSchema.properties as Record<string, JsonSchema>) ?? {};
  const required = new Set((branchSchema.required as string[] | undefined) ?? []);
  const result: Record<string, unknown> = {};
  Object.keys(props).forEach((key) => {
    if (required.has(key)) {
      result[key] = obj[key];
    }
  });
  return result;
}

/**
 * Renders a oneOf that can be enabled/disabled. When disabled, the section is hidden
 * and the property is removed from form data (undefined). When enabled, shows the
 * same dropdown + form as OneOfDropdownRenderer. Used for signal_parameters.
 */
export const EnableableOneOfRenderer = ({
  handleChange,
  schema,
  path,
  renderers,
  cells,
  rootSchema,
  id,
  visible,
  indexOfFittingSchema,
  uischema,
  uischemas,
  data,
}: CombinatorRendererProps) => {
  const enabled = data !== undefined && data !== null && typeof data === 'object';

  const oneOfRenderInfos = useMemo(
    () =>
      createCombinatorRenderInfos(
        (schema as JsonSchema).oneOf ?? [],
        rootSchema,
        'oneOf',
        uischema as ControlElement,
        path,
        uischemas
      ),
    [schema, rootSchema, uischema, path, uischemas]
  );

  const initialIndex =
    indexOfFittingSchema !== undefined && indexOfFittingSchema >= 0 ? indexOfFittingSchema : 0;
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  useEffect(() => {
    if (enabled && indexOfFittingSchema !== undefined && indexOfFittingSchema >= 0) {
      setSelectedIndex(indexOfFittingSchema);
    }
  }, [enabled, indexOfFittingSchema]);

  const handleEnableChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked && oneOfRenderInfos.length > 0) {
        const firstSchema = oneOfRenderInfos[0]?.schema;
        if (firstSchema) {
          const defaults = createDefaultValue(firstSchema, rootSchema);
          const requiredOnly = stripOptionalProperties(defaults, firstSchema as JsonSchema);
          handleChange(path, requiredOnly);
          setSelectedIndex(0);
        }
      } else {
        handleChange(path, undefined);
      }
    },
    [handleChange, path, rootSchema, oneOfRenderInfos]
  );

  const handleDropdownChange = useCallback(
    (event: { target: { value: string } }) => {
      const newIndex = Number(event.target.value);
      if (newIndex === selectedIndex) return;
      const newSchema = oneOfRenderInfos[newIndex]?.schema;
      if (newSchema) {
        const defaults = createDefaultValue(newSchema, rootSchema);
        const requiredOnly = stripOptionalProperties(defaults, newSchema as JsonSchema);
        handleChange(path, requiredOnly);
        setSelectedIndex(newIndex);
      }
    },
    [handleChange, path, rootSchema, oneOfRenderInfos, selectedIndex]
  );

  if (!visible) return null;
  if (!oneOfRenderInfos.length) return null;

  const controlElement = uischema as ControlElement;
  const rawLabel = controlElement?.label;
  const sectionLabel =
    typeof rawLabel === 'string'
      ? rawLabel
      : typeof rawLabel === 'object' && rawLabel !== null && 'text' in rawLabel
        ? (rawLabel as { text?: string }).text ?? 'Section'
        : 'Section';

  const typeLabel = `${sectionLabel} type`;
  const enableLabel = 'Enable';
  const selectedInfo = oneOfRenderInfos[selectedIndex];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={handleEnableChange}
            color="secondary"
            inputProps={{ 'aria-label': enableLabel }}
          />
        }
        label={enableLabel}
        sx={{ alignItems: 'center' }}
      />

      {enabled && selectedInfo && (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel id={`${id}-oneOf-label`}>{typeLabel}</InputLabel>
            <Select
              labelId={`${id}-oneOf-label`}
              value={String(selectedIndex)}
              label={typeLabel}
              onChange={handleDropdownChange}
            >
              {oneOfRenderInfos.map((info, idx) => (
                <MenuItem key={info.label} value={String(idx)}>
                  {info.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 0.5,
              width: '100%',
              boxSizing: 'border-box',
              bgcolor: 'action.hover',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {selectedInfo.label} config
            </Typography>
            <StrategyConfigForm
              schema={selectedInfo.schema}
              path={path}
              renderers={renderers}
              cells={cells}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

/** Resolve schema at control scope; __optionalSection is on the property schema, not the root. */
function resolveControlSchema(
  uischema: unknown,
  schema: unknown,
  context: { rootSchema?: unknown } | undefined
): Record<string, unknown> | undefined {
  const control = uischema as ControlElement;
  if (control?.type !== 'Control' || !control.scope) return undefined;
  const root = (context?.rootSchema ?? schema) as Record<string, unknown>;
  if (!root || typeof root !== 'object') return undefined;
  const scope = control.scope.startsWith('#')
    ? control.scope
    : `#/properties/${String(control.scope).replace(/\./g, '/properties/')}`;
  try {
    return Resolve.schema(
      root as Parameters<typeof Resolve.schema>[0],
      scope,
      root as Parameters<typeof Resolve.schema>[2]
    ) as Record<string, unknown> | undefined;
  } catch {
    return undefined;
  }
}

export const enableableOneOfTester = rankWith(
  6,
  (uischema, schema, context) => {
    const resolved = resolveControlSchema(uischema, schema, context);
    if (!resolved || !isOneOfControl(uischema, resolved, context)) return false;
    return (resolved as { __optionalSection?: boolean }).__optionalSection === true;
  }
);

export default withJsonFormsOneOfProps(EnableableOneOfRenderer);
